import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { GeneralTab } from "./sections/tabs/GeneralTab";
import { UpdatesTab } from "./sections/tabs/UpdatesTab";
import { UserExperienceTab } from "./sections/tabs/UserExperienceTab";
import { VulnerabilitiesTab } from "./sections/tabs/VulnerabilitiesTab";
import { AdvancedTab } from "./sections/tabs/AdvancedTab";
import { patchPolicySchema, type PatchPolicyFormValues } from "./schema";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

interface CreatePatchPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPolicyCreated?: () => void;
}

export function CreatePatchPolicyDialog({ open, onOpenChange, onPolicyCreated }: CreatePatchPolicyDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<PatchPolicyFormValues>({
    resolver: zodResolver(patchPolicySchema),
    defaultValues: {
      name: "",
      description: "",
      schedule_type: "manual",
      restart_required: false,
      defer_max_times: 3,
      defer_duration_minutes: 60,
      enabled: true,
      max_os_version: "",
      update_method: "default",
      force_install_days: 90,
      allowed_days: DAYS_OF_WEEK.map(day => day),
      show_notifications: true,
      notifications_timing: "all",
      allow_major_updates: true,
      allow_minor_updates: true,
      allow_security_updates: true,
      auto_download: true,
      auto_install_os_updates: false,
      auto_install_security_updates: true,
      enable_rsr_installation: false,
      enable_rsr_rollback: false,
      support_article_url: "",
      critical: {
        action: 'no_action',
      },
      high: {
        action: 'no_action',
      },
      medium: {
        action: 'no_action',
      },
      low: {
        action: 'no_action',
      },
      applyToActivelyExploitedOnly: false,
      vulnerabilityRestartBehavior: 'defer_available',
      vulnerabilityNotificationRecipients: [],
    },
  });

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  const onSubmit = async (values: PatchPolicyFormValues) => {
    setIsSubmitting(true);
    try {
      const policyData = {
        name: values.name,
        description: values.description,
        schedule_type: values.schedule_type,
        restart_required: values.restart_required,
        defer_max_times: values.defer_max_times,
        defer_duration_minutes: values.defer_duration_minutes,
        enabled: values.enabled,
        allowed_days: values.allowed_days,
        metadata: {
          max_os_version: values.max_os_version,
          update_method: values.update_method,
          force_install_days: values.force_install_days,
          show_notifications: values.show_notifications,
          notifications_timing: values.notifications_timing,
          allow_major_updates: values.allow_major_updates,
          allow_minor_updates: values.allow_minor_updates,
          allow_security_updates: values.allow_security_updates,
          auto_download: values.auto_download,
          auto_install_os_updates: values.auto_install_os_updates,
          auto_install_security_updates: values.auto_install_security_updates,
          enable_rsr_installation: values.enable_rsr_installation,
          enable_rsr_rollback: values.enable_rsr_rollback,
          support_article_url: values.support_article_url,
        },
        critical_vulnerability_action: values.critical?.action,
        critical_timeframe_value: values.critical?.timeframeValue,
        critical_timeframe_unit: values.critical?.timeframeUnit,
        critical_timezone: values.critical?.timezone,
        critical_time_of_day: values.critical?.timeOfDay,
        high_vulnerability_action: values.high?.action,
        high_timeframe_value: values.high?.timeframeValue,
        high_timeframe_unit: values.high?.timeframeUnit,
        high_timezone: values.high?.timezone,
        high_time_of_day: values.high?.timeOfDay,
        medium_vulnerability_action: values.medium?.action,
        medium_timeframe_value: values.medium?.timeframeValue,
        medium_timeframe_unit: values.medium?.timeframeUnit,
        medium_timezone: values.medium?.timezone,
        medium_time_of_day: values.medium?.timeOfDay,
        low_vulnerability_action: values.low?.action,
        low_timeframe_value: values.low?.timeframeValue,
        low_timeframe_unit: values.low?.timeframeUnit,
        low_timezone: values.low?.timezone,
        low_time_of_day: values.low?.timeOfDay,
        apply_to_actively_exploited_only: values.applyToActivelyExploitedOnly,
        vulnerability_restart_behavior: values.vulnerabilityRestartBehavior,
        vulnerability_notification_recipients: values.vulnerabilityNotificationRecipients,
      };

      const { error } = await supabase
        .from("patch_policies")
        .insert([policyData]);

      if (error) throw error;

      toast.success("Patch policy created successfully");
      queryClient.invalidateQueries({ queryKey: ["patch-policies"] });
      onPolicyCreated?.();
      handleClose();
    } catch (error) {
      console.error("Error creating patch policy:", error);
      toast.error("Failed to create patch policy");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Patch Policy</DialogTitle>
          <DialogDescription>
            Configure how updates should be handled across your devices
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="updates">Updates</TabsTrigger>
                <TabsTrigger value="experience">User Experience</TabsTrigger>
                <TabsTrigger value="vulnerabilities">Vulnerabilities</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="mt-6 min-h-[600px]">
                <GeneralTab form={form} />
              </TabsContent>

              <TabsContent value="updates" className="mt-6 min-h-[600px]">
                <UpdatesTab form={form} />
              </TabsContent>

              <TabsContent value="experience" className="mt-6 min-h-[600px]">
                <UserExperienceTab form={form} />
              </TabsContent>

              <TabsContent value="vulnerabilities" className="mt-6 min-h-[600px]">
                <VulnerabilitiesTab form={form} />
              </TabsContent>

              <TabsContent value="advanced" className="mt-6 min-h-[600px]">
                <AdvancedTab form={form} />
              </TabsContent>
            </Tabs>

            <DialogFooter className="flex gap-2 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Policy"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}